import React from 'react';
import { DiceValue } from '../../types';
import { calculateColumnScore } from '../../utils';
import Die from '../Die';
import { Shield } from 'lucide-react';
import { GridFx } from '../../hooks/useGridFx';
import { AnimatedNumber, ComboBurst, DieGhost, ScoreFloat, SlotFx } from './DiceFx';

interface ColumnProps {
  col: (DiceValue | null)[];
  index: number;
  canPlace: boolean;
  onClick: () => void;
  lastAction?: { col: number; target: 'player' | 'opponent' } | null;
  isPlayer: boolean;
  shieldRounds?: number;
  fx?: GridFx | null;
  /** Stagger delay (s) for the game-over tally pulse; null when not tallying */
  tallyDelay?: number | null;
}

export const Column: React.FC<ColumnProps> = ({ col, index, canPlace, onClick, lastAction, isPlayer, shieldRounds = 0, fx = null, tallyDelay = null }) => {
  const showShake = lastAction?.target === (isPlayer ? 'player' : 'opponent') && lastAction?.col === index;
  const isShielded = shieldRounds > 0;

  const placedHere = fx?.placed.find((p) => p.col === index) ?? null;
  const comboValue = placedHere && placedHere.combo >= 2 ? placedHere.value : null;
  const scoreDrop = fx?.scoreDrops.find((s) => s.col === index) ?? null;

  return (
    <div
      onClick={canPlace ? onClick : undefined}
      data-col
      className={`
        flex flex-col gap-1.5 lg:gap-4 p-1.5 lg:p-6 pixel-border transition-all cursor-pointer group relative
        ${canPlace ? 'bg-theme/40 border-theme shadow-theme scale-105' : 'bg-[#1a101f]'}
        ${isShielded ? '!border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.6)] bg-blue-900/30' : ''}
        ${showShake && !isShielded ? 'shake bg-red-900/40' : ''}
        ${tallyDelay != null ? 'column-tally' : ''}
      `}
      style={tallyDelay != null ? { animationDelay: `${tallyDelay}s` } : undefined}
    >
      {isShielded && (
        <div className="absolute -top-3 -right-3 lg:-top-4 lg:-right-4 z-10 bg-blue-600 rounded-full p-1 animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.8)] border-2 border-white flex flex-col items-center justify-center min-w-[24px] min-h-[24px] lg:min-w-[32px] lg:min-h-[32px]">
          <Shield color="white" fill="white" className="w-3 h-3 lg:w-4 lg:h-4" />
          <span className="text-[8px] lg:text-[10px] font-bold text-white leading-none mt-0.5">{shieldRounds}</span>
        </div>
      )}
      {col.map((die, di) => {
        const movedFrom = fx?.moved.find((m) => m.col === index && m.toRow === di)?.fromRow ?? null;
        const ghost = fx?.destroyed.find((d) => d.col === index && d.row === di) ?? null;
        return (
          <div key={di} data-slot className="relative">
            <SlotFx fxId={fx?.id} placed={placedHere?.row === di} movedFrom={movedFrom} row={di}>
              <Die
                value={die}
                size="md"
                className={die ? 'opacity-100' : 'opacity-20'}
                glow={die !== null && comboValue === die}
              />
            </SlotFx>
            {ghost && <DieGhost key={`ghost-${fx!.id}-${di}`} value={ghost.value} />}
          </div>
        );
      })}
      <div className="text-[8px] lg:text-lg text-center mt-1 lg:mt-3 font-bold text-yellow-500 font-mono">
        <AnimatedNumber value={calculateColumnScore(col)} flash />
      </div>
      {comboValue !== null && <ComboBurst key={`combo-${fx!.id}`} combo={placedHere!.combo} />}
      {scoreDrop && <ScoreFloat key={`drop-${fx!.id}`} amount={scoreDrop.amount} />}
    </div>
  );
};
